import * as Sentry from '@sentry/vue'
import { createSentryPiniaPlugin } from '@sentry/vue'

const UNAUTHORIZED = 401
const FORBIDDEN = 403
const SESSION_EXPIRED = 419
const SERVER_ERROR = 500

const getEnvironment = () => {
    const { host } = window.location
    const locations = {
        localhost: 'local-development',
        '0.0.0.0': 'local-development',
        'uat.linxfour.com': 'uat',
        'platform.linxfour.com': 'production',
    }
    return locations[host] || 'development'
}

const isLocalEnvironment = () => {
    return getEnvironment() === 'local-development'
}

const MISSING_TRANSLATION_ENVIRONMENTS = ['production', 'uat']

export const isMissingTranslationsReportingEnabled = () => {
    return MISSING_TRANSLATION_ENVIRONMENTS.includes(getEnvironment())
}

const abortLogging = (hint) => {
    if (isLocalEnvironment()) {
        return true
    }

    const error = hint.originalException
    const status = error?.response?.status
    if (
        status === UNAUTHORIZED ||
        status === SESSION_EXPIRED ||
        status === FORBIDDEN ||
        status === SERVER_ERROR
    ) {
        return true
    }

    return false
}

const getEnvironmentConfig = () => {
    const env = getEnvironment()

    const configs = {
        'local-development': {
            debug: true,
            tracesSampleRate: 0.0,
            replaysSessionSampleRate: 0.0,
            replaysOnErrorSampleRate: 0.0,
        },
        uat: {
            debug: false,
            tracesSampleRate: 0.1,
            replaysSessionSampleRate: 0.05,
            replaysOnErrorSampleRate: 0.5,
        },
        production: {
            debug: false,
            tracesSampleRate: 0.01,
            replaysSessionSampleRate: 0.01,
            replaysOnErrorSampleRate: 0.1,
        },
    }

    return configs[env] || configs['local-development']
}

export function initializeSentry(app, router, pinia) {
    if (isLocalEnvironment()) {
        console.log('Sentry disabled for local development')
        return
    }

    const envConfig = getEnvironmentConfig()

    Sentry.init({
        app,
        dsn: 'https://9681ed4a72094a5aa73df4543888ba82@o1154930.ingest.sentry.io/6235004',
        integrations: [
            Sentry.browserTracingIntegration({
                router,
                routeLabel: 'name',
                instrumentNavigation: true,
                instrumentPageLoad: true,
                beforeNavigate: (context) => {
                    const importantRoutes = [
                        'login',
                        'register',
                        'logout',
                        'contract',
                        'contract-create',
                        'contract-edit',
                        'invoice',
                        'invoice-create',
                        'invoice-edit',
                        'unit',
                        'unit-create',
                        'unit-edit',
                        'user',
                        'user-profile',
                        'user-invite',
                        'admin',
                        'settings',
                        'click-and-finance',
                        'PageNotFound',
                        'error',
                    ]

                    const routeName = context.to?.name

                    if (
                        routeName &&
                        importantRoutes.some((route) =>
                            routeName.toLowerCase().includes(route.toLowerCase())
                        )
                    ) {
                        return context
                    }

                    const skipRoutes = ['home', 'dashboard', 'index']
                    if (
                        routeName &&
                        skipRoutes.some((route) =>
                            routeName.toLowerCase().includes(route.toLowerCase())
                        )
                    ) {
                        return false
                    }

                    return context
                },
            }),
            Sentry.replayIntegration({
                maskAllText: getEnvironment() === 'production',
                blockAllMedia: getEnvironment() === 'production',
            }),
            Sentry.breadcrumbsIntegration({
                console: true,
                dom: true,
                fetch: true,
                history: true,
                sentry: true,
                xhr: true,
            }),
        ],

        trackComponents: true,
        environment: getEnvironment(),

        ...envConfig,

        beforeBreadcrumb(breadcrumb) {
            if (
                breadcrumb.category === 'pinia.action' &&
                breadcrumb.message?.includes('Store: missingTranslations')
            ) {
                return null
            }

            return breadcrumb
        },

        beforeSend(event, hint) {
            if (abortLogging(hint)) {
                return null
            }

            if (event.tags?.type === 'missing-translation') {
                hint.attachments = []
                if (event.contexts) {
                    delete event.contexts.state
                }

                return event
            }

            const error = hint.originalException
            if (error?.response?.status) {
                const status = error.response.status
                if (status === 401 || status === 419 || status === 403 || status === 422) {
                    return null
                }
            }

            const message = event.message || event.exception?.values?.[0]?.value || ''

            if (
                message.includes('NetworkError') ||
                message.includes('Failed to fetch') ||
                message.includes('Load failed') ||
                message.includes('Request failed with status code 401') ||
                message.includes('Request failed with status code 419') ||
                message.includes('Request failed with status code 403') ||
                message.includes('Request failed with status code 422') ||
                message.includes('Request failed with status code 500') ||
                message.includes('Validation failed')
            ) {
                return null
            }

            if (message.includes('Loading chunk') || message.includes('Loading CSS chunk')) {
                return null
            }

            if (event.exception && pinia) {
                const stores = {}
                pinia._s.forEach((store, key) => {
                    stores[key] = JSON.parse(JSON.stringify(store.$state))
                })

                event.extra = {
                    ...event.extra,
                    piniaStores: stores,
                }
            }

            return event
        },

        beforeSendTransaction(event) {
            if (isLocalEnvironment()) {
                return null
            }

            const transactionName = event.transaction || ''

            if (transactionName.includes('heartbeat') || transactionName.includes('health-check')) {
                return null
            }

            return event
        },
    })

    if (pinia) {
        const sentryPiniaPlugin = createSentryPiniaPlugin({
            stateTransformer: (state) => {
                const { password, token, ...sanitizedState } = state
                return sanitizedState
            },
        })

        pinia.use(sentryPiniaPlugin)
    }
}

export const reportBusinessError = (error, context = {}) => {
    if (isLocalEnvironment()) return

    Sentry.captureException(error, {
        tags: {
            type: 'business-logic',
            severity: 'high',
        },
        extra: {
            businessContext: context,
            timestamp: new Date().toISOString(),
        },
    })
}

export const reportAPIError = (error, endpoint, method) => {
    if (isLocalEnvironment()) return

    Sentry.captureException(error, {
        tags: {
            type: 'api-error',
            endpoint,
            method,
        },
        extra: {
            responseStatus: error?.response?.status,
            responseData: error?.response?.data,
        },
    })
}

export const addCustomBreadcrumb = (message, category, data = {}) => {
    if (isLocalEnvironment()) return

    Sentry.addBreadcrumb({
        category,
        message,
        level: 'info',
        data,
    })
}

export const reportMissingTranslations = ({
    keys,
    groups,
    routes,
    locales,
    reason,
    remaining = 0,
}) => {
    if (!isMissingTranslationsReportingEnabled()) return
    if (!Array.isArray(keys) || keys.length === 0) return

    Sentry.captureMessage(`Missing translations: ${keys.length} keys`, {
        level: 'warning',
        tags: {
            type: 'missing-translation',
            locale: locales?.length === 1 ? locales[0] : 'mixed',
            reason,
        },
        extra: {
            count: keys.length,
            remaining,
            keys,
            groups,
            routes,
            locales,
        },
        fingerprint: ['missing-translation-batch'],
    })
}

export const trackFileOperation = (operationType, fileName, details = {}) => {
    if (isLocalEnvironment()) return

    const startTime = Date.now()

    Sentry.addBreadcrumb({
        category: 'file-operation',
        message: `${operationType} started: ${fileName}`,
        level: 'info',
        data: {
            operation: operationType,
            fileName,
            ...details,
        },
    })

    return {
        success: () => {
            console.log(`File ${operationType} succeeded: ${fileName}`)
        },

        error: (error, additionalData = {}) => {
            const duration = Date.now() - startTime

            Sentry.captureException(error, {
                tags: {
                    type: 'file-operation-error',
                    operation: operationType,
                    fileName: fileName,
                },
                extra: {
                    operation: operationType,
                    fileName,
                    duration,
                    status: 'failed',
                    ...details,
                    ...additionalData,
                },
            })
        },
    }
}
